import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import { kebabCase } from '@makemydeal/dr-common-utils';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as flexBuyPaymentActionTypes from '@makemydeal/dr-dash-store/actionTypes/flexBuyPaymentActionTypes';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { FLEX_BUY_TERM_LIST } from '@makemydeal/dr-dash-store/constants';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import { FINANCE, FlexBuyAvailableMonths } from '@makemydeal/dr-platform-types';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FLEX_BUY_PERCENT_LABEL, GENERIC_LAST_FIELD_CHANGED_ERROR } from '../../constants';
import { flexBuyPercentOptions } from '../constants';
import { useGetFlexBuyCurrentMonths } from './useGetFlexBuyCurrentMonths';
import type { DealTabs } from '@makemydeal/dr-shared-types';

export const useFlexBuyPercentField = ({ tab }: { tab?: DealTabs }) => {
    const dispatch = useDispatch();
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isFlexBuyPaymentGridEnabled = useSelector(offerReduxSelectors.getFlexBuyPaymentGridToggle);
    const hasFailed = useSelector(
        compositeSelectors.getLastFailureActionIfError(flexBuyPaymentActionTypes.UPDATE_FLEX_BUY_PAYMENT)
    );
    const initialPaymentDiscount = useSelector(offerReduxSelectors.getOfferInitialPaymentDiscount);
    const currentFlexBuyPaymentGridTerms = useSelector(offerReduxSelectors.getFlexBuyPaymentGridTerms);
    const currentFlexBuyPaymentGridPercents = useSelector(offerReduxSelectors.getFlexBuyPaymentGridPercents);
    const currentFlexBuyPaymentGridCashDowns = useSelector(offerReduxSelectors.getFlexBuyPaymentGridCashDowns);
    const { flexBuyCurrentMonths: currentMonths } = useGetFlexBuyCurrentMonths();
    const options = useMemo(() => (currentMonths ? flexBuyPercentOptions[currentMonths] || [] : []), [currentMonths]);

    const parsedInitialPaymentDiscount =
        initialPaymentDiscount[0]?.toString() || flexBuyPercentOptions[FlexBuyAvailableMonths.MONTH_66][0].value.toString();

    const [value, setValue] = useState<string>(parsedInitialPaymentDiscount);

    const isSingle75MonthChoice = options.length === 1 && currentMonths === FlexBuyAvailableMonths.MONTH_75;
    const isDisabled = isSingle75MonthChoice || options.length === 0;

    useEffect(() => {
        if (parsedInitialPaymentDiscount !== value) setValue(parsedInitialPaymentDiscount);
    }, [parsedInitialPaymentDiscount, value]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && tab) {
            dispatch(dealScreenErrorActionCreators.setFieldError(tab, kebabCase(FLEX_BUY_PERCENT_LABEL), hasFailed));
        }
    }, [isDealScreenRedDotErrorsEnabled, dispatch, hasFailed, tab]);

    const updateFlexBuyPayment = (value: number) => {
        const meta = isFlexBuyPaymentGridEnabled
            ? {
                  paymentRequestOverride: {
                      [FINANCE]: {
                          flexBuyDownPayments: currentFlexBuyPaymentGridCashDowns,
                          flexBuyTerms: currentFlexBuyPaymentGridTerms,
                          flexBuyPercents: currentFlexBuyPaymentGridPercents,
                          termList: FLEX_BUY_TERM_LIST
                      }
                  }
              }
            : undefined;

        dispatch(
            offerReduxActionCreators.updateFlexBuyPaymentPercent(value, {
                meta,
                paymentGridOptions: {
                    gridAction: isFlexBuyPaymentGridEnabled,
                    isMainPayment: true
                }
            })
        );
    };

    const handleValueChange: InterstateOnChangeCallback<SelectInputEventValue> = (event) => {
        if (isSingle75MonthChoice) return;

        const selectedFlexBuyPercent = event.target.value;

        if (!selectedFlexBuyPercent) {
            return;
        }

        updateFlexBuyPayment(Number(selectedFlexBuyPercent));
        setValue(selectedFlexBuyPercent);
    };

    return {
        label: FLEX_BUY_PERCENT_LABEL,
        selectProps: {
            disabled: isDisabled,
            name: kebabCase(FLEX_BUY_PERCENT_LABEL),
            placeholder: 'Select Flex Buy Percent',
            onChange: handleValueChange,
            value,
            options,
            errorMessage: hasFailed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined,
            hasError: hasFailed,
            displayDeselectOption: false,
            'data-testid': kebabCase(FLEX_BUY_PERCENT_LABEL),
            size: 'sm' as const,
            readOnly: isFlexBuyPaymentGridEnabled
        }
    };
};
